package one.healthium.domain.user

import androidx.lifecycle.Lifecycle
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.mapLatest
import one.healthium.base.preferences.AppPreferences
import one.healthium.domain.health_log.models.HealthLogOption
import one.healthium.domain.user.migration.UserMigrationManager
import one.healthium.domain.user.models.FoodOptions
import one.healthium.domain.user.models.PlayTime
import one.healthium.domain.user.models.SignInOptions
import one.healthium.domain.user.models.SymptomsLogOptions
import one.healthium.domain.user.models.UserProfileModel
import one.healthium.domain.user.models.UserProfileOptions
import one.primehealth.app.AppLifecycleChannel
import kotlin.time.Clock
import kotlin.time.Instant

class UserInteractor(
    private val userRepository: UserRepository,
    private val userEvents: UserEvents?,
    private val userMigrationManager: UserMigrationManager,
    private val appPreferences: AppPreferences,
    private val appLifecycleChannel: AppLifecycleChannel,
) {
    suspend fun signIn(options: SignInOptions): UserModel {
        val userModel = userRepository.signIn(options)
        tryMigrate(userModel)
        return userModel
    }

    suspend fun requestMagicLink(email: String) {
        userRepository.requestMagicLink(email)
    }

    fun observeUser() = userRepository.getUser()

    suspend fun getUser(): UserModel {
        val userModel = userRepository.getUser().first()
        tryMigrate(userModel)
        return userModel
    }

    suspend fun updateUserProfile(userData: UserProfileModel) = userRepository.updateUserProfile(userData)
    suspend fun updateUserProfile(options: UserProfileOptions) {
        userRepository.updateUserProfile(options)

        // since initial weight is set during user creation into weight log as first entry so we have to update the value in weight log in case if initial weight edited
        val updatedWeight = options.weight
        if (updatedWeight != null) {
            getUser().weightLogs.minByOrNull { it.date }?.let { firstWeightLog ->
                userRepository.setWeightForDate(
                    date = firstWeightLog.date,
                    value = updatedWeight,
                )
            }
        }
    }

    suspend fun syncLocalToRemoteUser() = userRepository.syncLocalToRemoteUser()

    suspend fun addWaterIntake(value: Float, taskId: String? = null) {
        userRepository.addWaterIntake(value)
        userEvents?.onWaterIntakeAdded(taskId)
    }

    suspend fun addWeight(value: Float, taskId: String? = null) {
        userRepository.addWeight(value)
        userEvents?.onWeightUpdated(taskId)
    }

    suspend fun setWeightForDate(date: Instant, value: Float) {
        userRepository.setWeightForDate(date, value)
    }

    suspend fun removeWeight(date: Instant) {
        userRepository.removeWeight(date)
    }

    suspend fun addFood(options: FoodOptions, taskId: String? = null) {
        userRepository.addFood(options)
        userEvents?.onFoodAdded(taskId)
    }

    suspend fun setFoodForDate(dayId: String, foodId: String, options: FoodOptions) {
        userRepository.setFoodForDate(dayId, foodId, options)
    }

    suspend fun removeFood(dayId: String, foodId: String) {
        userRepository.removeFood(dayId, foodId)
    }

    suspend fun addSymptoms(options: SymptomsLogOptions, taskId: String? = null) {
        userRepository.addSymptoms(options)
        userEvents?.onSymptomsAdded(taskId)
    }

    suspend fun addHealthLog(options: List<HealthLogOption>, taskId: String? = null) {
        userRepository.addHealthLog(options)
        userEvents?.onHealthLogAdded(taskId)
    }

    private suspend fun tryMigrate(user: UserModel) {
        userMigrationManager.tryMigrate(user)
    }

    fun syncHealthData(): Flow<Unit> {
        return appLifecycleChannel.lifecycle
            .filter { it.isAtLeast(Lifecycle.State.STARTED) }
            .mapLatest {
                getUser().weightLogs.lastOrNull()?.date?.let { lastStoredWeightDate ->
                    userRepository.syncHealthData(lastStoredWeightDate)
                }
            }
    }

    fun updateSystemData(): Flow<Unit> {
        return appLifecycleChannel.lifecycle
            .mapLatest { lifecycleState ->
                when (lifecycleState) {
                    Lifecycle.State.STARTED -> {
                        //App entered Foreground
                        appPreferences.playTimeLastSessionStartTimestamp = Clock.System.now().epochSeconds
                    }

                    Lifecycle.State.CREATED -> {
                        //App entered Background
                        updatePlayTime()
                    }

                    else -> {}
                }
            }
    }

    suspend fun setPeriods(periods: List<Instant>) {
        userRepository.setPeriodData(periods)
    }

    suspend fun setAvatar(avatar: ByteArray) {
        userRepository.setAvatar(avatar)
    }

    suspend fun deleteAvatar() {
        userRepository.deleteAvatar()
    }

    private suspend fun updatePlayTime() {
        val duration = appPreferences.playTimeDuration ?: 0
        appPreferences.playTimeLastSessionStartTimestamp?.let { startTime ->
            val endTime = Clock.System.now().epochSeconds
            val newDuration = duration + (endTime - startTime)
            appPreferences.playTimeDuration = newDuration
            val previousPlayTime = PlayTime.fromSeconds(duration)
            val currentPlayTime = PlayTime.fromSeconds(newDuration)
            if (previousPlayTime < currentPlayTime) {
                userRepository.updatePlayTime(currentPlayTime.code)
            }
        }
    }
}
